import { create } from 'zustand';
import type { ItemUnit } from '@pantry/shared';

/**
 * A pending meal prep item that will be created when meal prep is submitted.
 * This represents items scanned that don't exist in the user's inventory yet.
 */
export interface PendingMealPrepItem {
  /** Local UUID for tracking this pending item */
  clientId: string;
  /** Scanned barcode (if any) */
  barcode?: string;
  /** Product ID if found in local database or OFF */
  productId?: string;
  /** Product name from lookup */
  productName?: string;
  /** Product brand from lookup */
  productBrand?: string;
  /** Product image URL from lookup */
  productImageUrl?: string;
  /** User-entered custom name (overrides productName) */
  customName?: string;
  /** Total quantity available (what gets added to inventory) */
  quantity: number;
  /** Quantity to use/consume in this session (defaults to quantity if not set) */
  quantityToUse?: number;
  /** Unit of measurement (default: 'item') */
  unit: ItemUnit;
  /** Expiry date as ISO string (optional) */
  expiryDate?: string;
  /** Storage location ID (required for submission) */
  storageLocationId?: string;
}

/**
 * Zustand store for meal prep mode state.
 * Stores only IDs - actual item/instance data is looked up from React Query cache.
 * This prevents stale data issues after updates.
 */
interface MealPrepState {
  /** Whether meal prep mode is active */
  isActive: boolean;
  /** Set of selected instance IDs */
  selectedInstanceIds: Set<string>;
  /** Map from instanceId to itemId for reverse lookup */
  instanceToItemId: Map<string, string>;
  /** Pending items to be created when meal prep is submitted */
  pendingItems: PendingMealPrepItem[];

  /** Activate meal prep mode */
  activate: () => void;
  /** Deactivate meal prep mode and clear selections */
  deactivate: () => void;
  /** Toggle selection of an item instance by IDs */
  toggleSelection: (itemId: string, instanceId: string) => void;
  /** Check if an instance is selected */
  isSelected: (instanceId: string) => boolean;
  /** Clear all selections without deactivating */
  clearSelection: () => void;
  /** Get count of selected items */
  getSelectionCount: () => number;
  /** Get all selected instance IDs */
  getSelectedInstanceIds: () => string[];
  /** Get the item ID for a given instance ID */
  getItemIdForInstance: (instanceId: string) => string | undefined;

  // Pending items methods
  /** Add a pending item and return its clientId */
  addPendingItem: (item: Omit<PendingMealPrepItem, 'clientId'>) => string;
  /** Update a pending item */
  updatePendingItem: (clientId: string, updates: Partial<PendingMealPrepItem>) => void;
  /** Remove a pending item */
  removePendingItem: (clientId: string) => void;
  /** Get all pending items */
  getPendingItems: () => PendingMealPrepItem[];
  /** Get count of pending items */
  getPendingItemsCount: () => number;
  /** Check if there are any pending items */
  hasPendingItems: () => boolean;
  /** Check if all pending items are valid for submission */
  areAllPendingItemsValid: () => boolean;
  /** Clear all pending items */
  clearPendingItems: () => void;
}

/** Generate a simple UUID for client-side tracking */
function generateClientId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Validate a pending item: must have (productId || customName) && storageLocationId */
function isPendingItemValid(item: PendingMealPrepItem): boolean {
  const hasIdentity = !!(item.productId || item.customName);
  const hasLocation = !!item.storageLocationId;
  return hasIdentity && hasLocation;
}

export const useMealPrepStore = create<MealPrepState>((set, get) => ({
  isActive: false,
  selectedInstanceIds: new Set(),
  instanceToItemId: new Map(),
  pendingItems: [],

  activate: () => set({ isActive: true }),

  deactivate: () =>
    set({
      isActive: false,
      selectedInstanceIds: new Set(),
      instanceToItemId: new Map(),
      pendingItems: [],
    }),

  toggleSelection: (itemId, instanceId) =>
    set((state) => {
      const newIds = new Set(state.selectedInstanceIds);
      const newMap = new Map(state.instanceToItemId);
      if (newIds.has(instanceId)) {
        newIds.delete(instanceId);
        newMap.delete(instanceId);
      } else {
        newIds.add(instanceId);
        newMap.set(instanceId, itemId);
      }
      return { selectedInstanceIds: newIds, instanceToItemId: newMap };
    }),

  isSelected: (instanceId) => get().selectedInstanceIds.has(instanceId),

  clearSelection: () =>
    set({ selectedInstanceIds: new Set(), instanceToItemId: new Map(), pendingItems: [] }),

  getSelectionCount: () => get().selectedInstanceIds.size,

  getSelectedInstanceIds: () => [...get().selectedInstanceIds],

  getItemIdForInstance: (instanceId) => get().instanceToItemId.get(instanceId),

  // Pending items methods
  addPendingItem: (item) => {
    const clientId = generateClientId();
    set((state) => ({
      pendingItems: [...state.pendingItems, { ...item, clientId }],
    }));
    return clientId;
  },

  updatePendingItem: (clientId, updates) =>
    set((state) => ({
      pendingItems: state.pendingItems.map((item) =>
        item.clientId === clientId ? { ...item, ...updates } : item
      ),
    })),

  removePendingItem: (clientId) =>
    set((state) => ({
      pendingItems: state.pendingItems.filter((item) => item.clientId !== clientId),
    })),

  getPendingItems: () => get().pendingItems,

  getPendingItemsCount: () => get().pendingItems.length,

  hasPendingItems: () => get().pendingItems.length > 0,

  areAllPendingItemsValid: () => {
    const items = get().pendingItems;
    if (items.length === 0) return true;
    return items.every(isPendingItemValid);
  },

  clearPendingItems: () => set({ pendingItems: [] }),
}));
